// The filter bar (docs/SPEC.md item 6; web/controls.js renderFilterBar): Free, Price, Types, edge to edge, no
// scrolling. Free and a price exclude each other (RULES.freePrice). The price slider and the Types list open as
// popovers under their buttons; while one is open the floating map controls fade away (body.filters-open), and on
// the phone the Types list slides whatever is at the bottom off the screen (body.types-open).
import { useEffect, useRef, useState } from "react";
import { SHARED, T } from "../../shared/config";
import type { IconName } from "../../shared/icons";
import { RULES } from "../../shared/rules";
import { tagEmoji, useFeed } from "../../state/feed";
import { useProfileView } from "../../state/profile";
import { setFreePrice, setNearbyExpanded, setPopover, useView, viewStore } from "../../state/view";
import { closeLevelMenu } from "../profile/slots";
import { FilterChip } from "./FilterChip";
import { PricePopover, priceLabel } from "./PricePopover";
import { TypesPopover } from "./TypesPopover";
import styles from "./FilterBar.module.css";

export function FilterBar() {
  const f = useView((s) => s.filter), popover = useView((s) => s.popover);
  const tags = useFeed((s) => s.tags), levels = useProfileView().levels;
  const priceBtn = useRef<HTMLButtonElement>(null), typeBtn = useRef<HTMLButtonElement>(null);
  const ordered = RULES.typeOrder(tags, levels as Map<string, string>);
  const tb = RULES.typeButton(ordered, f.tags as Set<string>, f.allInterests, (t) => tagEmoji(t) || SHARED.fallbackEmoji);
  // A popover that's closing stays drawn until its fade-out ends.
  const [leaving, setLeaving] = useState<"price" | "types" | null>(null);
  const close = (animate: boolean) => {
    const open = viewStore.getState().popover;
    if (!open) return;
    if (animate) setLeaving(open);
    setPopover(null);
  };

  useEffect(() => {
    document.body.classList.toggle("filters-open", !!popover);
    document.body.classList.toggle("types-open", popover === "types");
    // Price: the list drops to its bar.
    if (popover === "price") setNearbyExpanded(false);
  }, [popover]);

  // Tapping outside a popover (and its button), Escape, or resizing the window closes it.
  useEffect(() => {
    if (!popover) return;
    const down = (ev: PointerEvent) => {
      const t = ev.target as Element;
      if (t.closest?.("#price-pop, #type-pop, #price-chip, #type-chip")) return;
      close(false);
    };
    // Escape closes the innermost thing: a type's level menu, then the dropdown.
    const key = (ev: KeyboardEvent) => {
      if (ev.key !== "Escape" || ev.defaultPrevented) return;
      ev.preventDefault();
      if (!closeLevelMenu()) close(false);
    };
    const resize = () => close(false);
    document.addEventListener("pointerdown", down);
    document.addEventListener("keydown", key);
    addEventListener("resize", resize);
    return () => { document.removeEventListener("pointerdown", down); document.removeEventListener("keydown", key); removeEventListener("resize", resize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popover]);

  const toggle = (which: "price" | "types") => { setLeaving(null); setPopover(popover === which ? null : which); };

  return (
    <div id="chips" className={styles.chips}>
      {/* The icons echo the pins: Free is a squircle gift, the price a squircle ticket. */}
      <FilterChip id="free-chip" on={f.freeOnly} lead="free-gift" label={T("filter.free")} aria-pressed={f.freeOnly}
        onClick={() => setFreePrice({ freeOnly: !f.freeOnly })} />
      <FilterChip ref={priceBtn} id="price-chip" on={f.maxPrice != null} lead="price-ticket" label={priceLabel(f.maxPrice)} dropdown
        aria-haspopup="dialog" aria-expanded={popover === "price"} onClick={() => toggle("price")} />
      <FilterChip ref={typeBtn} id="type-chip" className={styles.types} on={tb.label !== "filter.anyType"} dropdown
        lead={tb.lead as IconName | null} label={tb.label ? T(tb.label) : null} labelIcon={tb.icon as IconName | null}
        emojis={tb.label ? null : tb.emojis}
        aria-label={tb.icon ? T("filter.allInterests") : tb.label ? undefined : tb.emojis.join(" ")}
        aria-haspopup="listbox" aria-expanded={popover === "types"} onClick={() => toggle("types")} />
      {(popover === "price" || leaving === "price") &&
        <PricePopover anchor={priceBtn} leaving={leaving === "price" && popover !== "price"} onGone={() => setLeaving(null)} onAutoClose={() => close(true)} />}
      {(popover === "types" || leaving === "types") &&
        <TypesPopover anchor={typeBtn} ordered={ordered} leaving={leaving === "types" && popover !== "types"} onGone={() => setLeaving(null)} />}
    </div>
  );
}
